import React from "react";
import { useRecordCrumb } from "@/components/shell/ShellContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BarChart3, CheckCircle2, ClipboardList, FileText, History, Inbox, Layers, ListChecks, Percent, Scale, Target, TriangleAlert, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { DateRangeFilter, buildQuery, useDateRange, useReport } from "@/components/reports/DateRangeFilter";
import { ExportMenu, type ExportFormat } from "@/components/reports/ExportMenu";
import {
  ColumnChart,
  DataTable,
  ReportSkeleton,
  SectionCard,
  StatTile,
  StatusPill,
  TargetBar,
  achievementTone,
  fmtDate,
  fmtMoney,
  fmtMonth,
  fmtNum,
  fmtPct,
  type Tone,
} from "@/components/reports/ReportWidgets";
import type { ReportUser, UserMetrics } from "@/types/report";

interface UserDetail {
  user: ReportUser;
  metrics: UserMetrics;
  trend: Array<{ period: string; deals_won: number; won_amount: number }>;
  open_deals: Array<{ id: string; name: string; amount: number; expected_close_date: string; stage_name: string; account_name: string; updated_on: string }>;
  open_leads: Array<{ id: string; name: string; company_name: string; lead_status: string; priority: string; score: number; created_on: string; last_contacted_at: string }>;
  overdue_tasks: Array<{ id: string; title: string; related_type: string; related_id: string; related_name: string; due_at: string }>;
  recent_activities: Array<{ id: string; activity_type: string; title: string; created_on: string; deal_id: string; deal_name: string }>;
  recent_quotes: Array<{ id: string; display_number: string; quote_number: string; quote_status: string; grand_total: number; max_discount_pct: number; created_on: string }>;
}

const LEAD_STATUS: Record<string, string> = { NEW: "New", CONTACTED: "Contacted", FOLLOW_UP: "Follow-up", QUALIFIED: "Qualified" };
const PRIORITY: Record<string, { label: string; tone: Tone }> = { HOT: { label: "Hot", tone: "red" }, WARM: { label: "Warm", tone: "orange" }, COLD: { label: "Cold", tone: "blue" } };
const QUOTE: Record<string, { label: string; tone: Tone }> = {
  DRAFT: { label: "Draft", tone: "gray" },
  PENDING_APPROVAL: { label: "Waiting for approval", tone: "orange" },
  APPROVED: { label: "Approved", tone: "blue" },
  SENT: { label: "Sent", tone: "purple" },
  ACCEPTED: { label: "Accepted", tone: "green" },
  REJECTED: { label: "Rejected", tone: "red" },
};
const ACTIVITY: Record<string, string> = { CALL: "Call", MEETING: "Meeting", EMAIL: "Email", WHATSAPP: "WhatsApp", NOTE: "Note" };

export function UserReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { preset, range, choosePreset, setRange } = useDateRange("this_month");
  const { data, loading, error } = useReport<UserDetail>(id ? `/api/Report/UserDetail/${id}${buildQuery({ from: range.from, to: range.to })}` : null);
  // The file holds exactly this page: the same person and period
  const fileUrl = (format: ExportFormat) => `/api/Report/Download/user-detail${buildQuery({ format, from: range.from, to: range.to, user_id: id || "" })}`;

  useRecordCrumb(data?.user?.name);
  const m = data?.metrics;
  const today = new Date(new Date().toDateString());

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h2 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
              {data?.user.name || "Loading…"}
              {data && <Badge variant="outline" className="text-[10px]">{data.user.role_name}</Badge>}
              {data && !data.user.active && <StatusPill tone="gray">Inactive</StatusPill>}
            </h2>
            {data && (
              <p className="text-sm text-muted-foreground">
                {data.user.email}
                {data.user.manager_name && <> · reports to {data.user.manager_name}</>}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeFilter preset={preset} range={range} onPreset={choosePreset} onRange={setRange} />
          {hasPermission("report.export") && data && <ExportMenu fileUrl={fileUrl} />}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading && !data && !error && <ReportSkeleton tiles={8} cards={4} />}

      {data && m && (
        <>
          <div className="grid grid-cols-2 @4xl/main:grid-cols-4 gap-3">
            <StatTile label="Revenue won" value={fmtMoney(m.won_amount)} tone="green" icon={<Trophy />} hint={<span>{m.deals_won} deal{m.deals_won === 1 ? "" : "s"} · avg {fmtMoney(m.avg_deal_size)}</span>} />
            <StatTile
              label="Target"
              value={m.target_achieved_pct === null ? "—" : fmtPct(m.target_achieved_pct)}
              tone={achievementTone(m.target_achieved_pct)}
              icon={<Target />}
              hint={m.target_amount ? <TargetBar achieved={m.won_amount} target={m.target_amount} /> : <span>No target for this period</span>}
            />
            <StatTile label="Lead conversion" value={fmtPct(m.conversion_pct)} tone="purple" icon={<Percent />} hint={<span>{m.leads_converted} of {m.leads_assigned} leads · {m.leads_untouched} untouched</span>} />
            <StatTile label="Win rate" value={fmtPct(m.win_rate_pct)} tone="blue" icon={<Scale />} hint={<span>{m.deals_won} won · {m.deals_lost} lost · {m.avg_cycle_days}d cycle</span>} />
            <StatTile label="Open pipeline" value={fmtMoney(m.pipeline_amount)} tone="blue" icon={<Layers />} hint={<span>{m.open_deals} deals · {fmtMoney(m.weighted_amount)} weighted</span>} />
            <StatTile label="Quotes" value={fmtNum(m.quotes_sent)} tone="purple" icon={<FileText />} hint={<span>sent · {m.quotes_accepted} accepted · {fmtPct(m.avg_discount_pct)} avg disc.</span>} />
            <StatTile label="Activities" value={fmtNum(m.calls + m.meetings + m.emails + m.whatsapp + m.notes)} tone="blue" icon={<ListChecks />} hint={<span>{m.calls} calls · {m.meetings} meetings</span>} />
            <StatTile label="Tasks done" value={fmtNum(m.tasks_done)} tone={m.tasks_overdue ? "orange" : "green"} icon={<CheckCircle2 />} hint={<span className={m.tasks_overdue ? "font-semibold text-danger" : undefined}>{m.tasks_overdue} overdue now</span>} />
          </div>

          <SectionCard title="Revenue won" description="Last 6 months" icon={<BarChart3 />} tone="green">
            <ColumnChart
              data={data.trend.map((t) => ({ label: fmtMonth(t.period), value: t.won_amount, detail: [`${t.deals_won} deals`] }))}
              format={(v) => fmtMoney(v)}
              height={140}
              emptyMessage="No deals won in the last 6 months."
            />
          </SectionCard>

          <div className="grid grid-cols-1 @4xl/main:grid-cols-2 gap-4">
            <SectionCard title="Open deals" description="Largest first, as of now" icon={<Layers />}>
              <DataTable
                columns={[
                  { key: "name", header: "Deal", render: (r) => <Link to={`/deals/${r.id}`} className="font-medium text-primary hover:underline">{r.name}</Link> },
                  { key: "stage_name", header: "Stage" },
                  {
                    key: "expected_close_date",
                    header: "Expected",
                    render: (r) => (r.expected_close_date && new Date(r.expected_close_date) < today ? <StatusPill tone="red">{fmtDate(r.expected_close_date)}</StatusPill> : fmtDate(r.expected_close_date)),
                  },
                  { key: "amount", header: "Amount", align: "right", render: (r) => <span className="font-semibold">{fmtMoney(r.amount)}</span> },
                ]}
                rows={data.open_deals}
                rowKey={(r) => r.id}
                emptyMessage="No open deals."
              />
            </SectionCard>
            <SectionCard title="Open leads" description="Newest first" icon={<Inbox />} tone="purple">
              <DataTable
                columns={[
                  { key: "name", header: "Lead", render: (r) => <Link to={`/leads/${r.id}`} className="font-medium text-primary hover:underline">{r.name || r.company_name}</Link> },
                  { key: "lead_status", header: "Status", render: (r) => LEAD_STATUS[r.lead_status] || r.lead_status },
                  { key: "priority", header: "Priority", render: (r) => (PRIORITY[r.priority] ? <StatusPill tone={PRIORITY[r.priority]!.tone}>{PRIORITY[r.priority]!.label}</StatusPill> : "—") },
                  { key: "score", header: "Score", align: "right" },
                  { key: "last_contacted_at", header: "Last contact", render: (r) => fmtDate(r.last_contacted_at) },
                ]}
                rows={data.open_leads}
                rowKey={(r) => r.id}
                emptyMessage="No open leads."
              />
            </SectionCard>
            <SectionCard title="Overdue tasks" icon={<TriangleAlert />} tone={data.overdue_tasks.length ? "red" : "green"}>
              <DataTable
                columns={[
                  { key: "title", header: "Task", render: (r) => <span className="font-medium text-foreground">{r.title}</span> },
                  { key: "related_name", header: "For", render: (r) => r.related_name || "—" },
                  { key: "due_at", header: "Due", render: (r) => <span className="font-semibold text-danger">{fmtDate(r.due_at)}</span> },
                ]}
                rows={data.overdue_tasks}
                rowKey={(r) => r.id}
                emptyMessage="No overdue tasks."
              />
            </SectionCard>
            <SectionCard title="Recent activity" icon={<History />}>
              <DataTable
                columns={[
                  { key: "created_on", header: "When", render: (r) => fmtDate(r.created_on) },
                  { key: "activity_type", header: "Type", render: (r) => ACTIVITY[r.activity_type] || r.activity_type.toLowerCase() },
                  { key: "title", header: "Title", render: (r) => <span className="text-foreground">{r.title}</span> },
                  { key: "deal_name", header: "Deal", render: (r) => (r.deal_id ? <Link to={`/deals/${r.deal_id}`} className="text-primary hover:underline">{r.deal_name}</Link> : "—") },
                ]}
                rows={data.recent_activities}
                rowKey={(r) => r.id}
                emptyMessage="No activity logged."
              />
            </SectionCard>
            <SectionCard title="Recent quotes" className="@4xl/main:col-span-2" icon={<ClipboardList />} tone="purple">
              <DataTable
                columns={[
                  { key: "display_number", header: "Quote", render: (r) => <Link to={`/quotes/${r.id}`} className="font-medium text-primary hover:underline">{r.display_number || r.quote_number}</Link> },
                  { key: "quote_status", header: "Status", render: (r) => (QUOTE[r.quote_status] ? <StatusPill tone={QUOTE[r.quote_status]!.tone}>{QUOTE[r.quote_status]!.label}</StatusPill> : r.quote_status.replace(/_/g, " ").toLowerCase()) },
                  { key: "created_on", header: "Created", render: (r) => fmtDate(r.created_on) },
                  { key: "max_discount_pct", header: "Max disc.", align: "right", render: (r) => fmtPct(r.max_discount_pct) },
                  { key: "grand_total", header: "Total", align: "right", render: (r) => <span className="font-semibold">{fmtMoney(r.grand_total)}</span> },
                ]}
                rows={data.recent_quotes}
                rowKey={(r) => r.id}
                emptyMessage="No quotes."
              />
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}
